import { useQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import { FC, useCallback, useMemo } from 'react'
import { BROADCAST_CHANNEL, DEBOUNCE_DELAY } from 'constants/consts'
import { Autosuggest, AutosuggestProps } from 'components/Autosuggest/Autosuggest'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { SEARCH_BY_GUIDE_TITLE } from 'queries/studyGuide'
import {
  searchByGuideTitle,
  searchByGuideTitleVariables,
  searchByGuideTitle_searchByGuideTitle_items as StudyGuide,
} from '__generatedTypes__/searchByGuideTitle'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { REFETCH_STUDY_GUIDES_MESSAGE } from 'pagesNew/StudyGuide/Create'
import { StudyGuideRenderItem } from './StudyGuideRenderItem'

type Props = {
  platform: Platform
  value: StudyGuide[]
  onAdd: (studyGuide: StudyGuide) => void
} & Partial<AutosuggestProps<StudyGuide>>

export const StudyGuidesSearch: FC<Props> = ({ platform, value, onAdd, ...rest }) => {
  const platformId = platform?.id

  const { data, refetch: refetchStudyGuides } = useQuery<searchByGuideTitle, searchByGuideTitleVariables>(
    SEARCH_BY_GUIDE_TITLE,
    {
      fetchPolicy: 'network-only',
      variables: {
        searchString: '',
        platformId,
      },
    }
  )

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_STUDY_GUIDES_MESSAGE) {
      refetchStudyGuides()
    }
  })

  const handleStorytellerInputChange = useCallback(
    debounce((searchString: string) => {
      refetchStudyGuides({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const filteredStudyGuides = useMemo(() => {
    const addedStudyGuidesIds = value.map(({ id }) => id)

    return (data?.searchByGuideTitle?.items ?? []).filter(({ id }) => !addedStudyGuidesIds.includes(id))
  }, [value, data])

  return (
    <Autosuggest<StudyGuide>
      items={filteredStudyGuides}
      onInputChange={handleStorytellerInputChange}
      getItemLabel={(studyGuide) => studyGuide.title}
      renderItem={(studyGuide) => <StudyGuideRenderItem studyGuide={studyGuide} />}
      placeholder="Search Article"
      searchable
      withoutCheckMark
      fullWidth
      onChange={(value) => onAdd(value[0])}
      value={value}
      {...rest}
    />
  )
}
